'use client';
import { Popover, Transition } from '@headlessui/react';
import {
    ArrowTopRightOnSquareIcon,
    ExclamationCircleIcon,
    RocketLaunchIcon,
} from '@heroicons/react/20/solid';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor } from '../context/EditorContext/EditorContext';
import { useNotifications } from '../context/NotificationContext/NotificationContext';
import { collectUsedImages } from '../helpers/altTextAudit';
import {
    LARGE_IMAGE_SIZE_LIMIT,
    readImageSizeBytes,
} from '../helpers/imageSize';
import { API } from '../utils/api';
import { AltTextEditor } from './AltTextEditor';
import { ConfirmationModal } from '../ConfirmationModal';

const PUBLISH_POLL_INTERVAL_MS = 2500;
const PUBLISH_TIMEOUT_MS = 10 * 60 * 1000;
const FAILED_DEPLOYMENT_STATES = new Set(['ERROR', 'CANCELED', 'CANCELLED']);

const wait = (duration) =>
    new Promise((resolve) => window.setTimeout(resolve, duration));

async function waitForPublishDeployment(companyId, deploymentId, isCurrent) {
    const startedAt = Date.now();
    let consecutivePollFailures = 0;

    while (Date.now() - startedAt < PUBLISH_TIMEOUT_MS) {
        await wait(PUBLISH_POLL_INTERVAL_MS);
        if (!isCurrent()) return null;

        let deployment;
        try {
            deployment = await API.get(
                `${companyId}/publish/${encodeURIComponent(deploymentId)}`
            );
            consecutivePollFailures = 0;
        } catch (error) {
            consecutivePollFailures += 1;
            if (consecutivePollFailures >= 3) throw error;
            continue;
        }
        const status = String(deployment?.status || '').toUpperCase();

        if (status === 'READY') return deployment;
        if (FAILED_DEPLOYMENT_STATES.has(status)) {
            throw new Error(`Vercel deployment ${status.toLowerCase()}`);
        }
    }

    throw new Error('Publishing timed out while waiting for Vercel');
}

function PublishOutsideClick({ open, close, buttonRef, panelRef }) {
    useEffect(() => {
        if (!open) return;

        const ownerDoc = buttonRef.current?.ownerDocument;
        if (!ownerDoc) return;

        const isInsideRef = (ref, target) => {
            const el = ref.current;
            if (!el || !target) return false;
            try {
                return el === target || el.contains(target);
            } catch {
                return false;
            }
        };

        const handlePointerDown = (event) => {
            const target = event.target;
            if (!target || typeof target !== 'object') return;

            if (isInsideRef(buttonRef, target)) return;
            if (isInsideRef(panelRef, target)) return;

            close();
        };

        const docs = new Set([ownerDoc]);
        ownerDoc.querySelectorAll('iframe').forEach((frame) => {
            try {
                if (frame.contentDocument) docs.add(frame.contentDocument);
            } catch {
                // Ignore cross-origin frames
            }
        });

        docs.forEach((doc) => {
            doc.addEventListener('pointerdown', handlePointerDown, true);
        });

        return () => {
            docs.forEach((doc) => {
                doc.removeEventListener('pointerdown', handlePointerDown, true);
            });
        };
    }, [open, close, buttonRef, panelRef]);

    return null;
}

function extractHostname(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';

    try {
        if (/^https?:\/\//i.test(raw)) {
            return new URL(raw).hostname;
        }
        return new URL(`https://${raw}`).hostname;
    } catch {
        return raw.replace(/^https?:\/\//i, '').split('/')[0];
    }
}

function normalizeSiteUrl(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';

    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function slugifyPreview(value) {
    return String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 63);
}

export function Publish({ buttonClass = '', icon = null } = {}) {
    const editor = useEditor();
    const notifications = useNotifications();
    const { company_id, company_name, domain, slug, website } =
        editor.global.data;
    const [loading, setLoading] = useState(false);
    const [publishPhase, setPublishPhase] = useState('idle');
    const [publicationStatusKnown, setPublicationStatusKnown] = useState(false);
    const [canUnpublish, setCanUnpublish] = useState(false);
    const [unpublishOpen, setUnpublishOpen] = useState(false);
    const [unpublishing, setUnpublishing] = useState(false);
    const [publishDestinations, setPublishDestinations] = useState({
        production: true,
        staging: false,
    });
    const [mounted, setMounted] = useState(false);
    const [altTextPanelOpen, setAltTextPanelOpen] = useState(false);
    const [altTextPanelFocus, setAltTextPanelFocus] = useState('alt-text');
    const [altTextSources, setAltTextSources] = useState([]);
    const [imageSizesByUrl, setImageSizesByUrl] = useState({});
    const [hasCustomFavicon, setHasCustomFavicon] = useState(null);
    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const publishAttemptRef = useRef(0);
    const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
    const liveDomain =
        extractHostname(domain) ||
        extractHostname(website) ||
        'www.clientsdomain.com';
    const liveUrl = normalizeSiteUrl(domain || website || liveDomain);
    const previewSlug =
        slugifyPreview(slug) || slugifyPreview(company_name) || company_id;
    const previewDomain = `${previewSlug}.nicejob.site`;
    const apexLiveDomain = liveDomain.replace(/^www\./i, '');
    const currentPageName = editor.page.name || 'index';
    const effectiveAltTextSources = useMemo(() => {
        const loadedSources = altTextSources.length
            ? altTextSources
            : editor.components.data.map((component) => ({
                  id: `${currentPageName}:${component.data_file}`,
                  pageName: currentPageName,
                  dataFile: component.data_file,
                  componentName: component.name,
                  props: component.props,
                  global: ['nav.json', 'footer.json'].includes(
                      component.data_file
                  ),
              }));

        return loadedSources.map((source) => {
            const currentComponent = editor.components.data.find(
                (component) => component.data_file === source.dataFile
            );
            const belongsToCurrentEditor =
                source.global || source.pageName === currentPageName;
            if (!currentComponent || !belongsToCurrentEditor) return source;

            const props = structuredClone(currentComponent.props);
            delete props.company;
            delete props.brand;
            return { ...source, props };
        });
    }, [altTextSources, currentPageName, editor.components.data]);
    const usedImages = useMemo(
        () =>
            collectUsedImages(
                effectiveAltTextSources.map((source) => ({
                    sourceId: source.id,
                    pageName: source.pageName,
                    data_file: source.dataFile,
                    name: source.componentName,
                    props: source.props,
                }))
            ),
        [effectiveAltTextSources]
    );
    const missingAltTextCount = usedImages.filter(
        (image) => image.missingAltText
    ).length;
    const hasMissingAltText = missingAltTextCount > 0;
    const usedImageUrlsKey = usedImages.map((image) => image.url).join('\n');
    const heavyImageCount = usedImages.filter(
        (image) =>
            Number(imageSizesByUrl[image.url] || 0) > LARGE_IMAGE_SIZE_LIMIT
    ).length;
    const hasHeavyImages = heavyImageCount > 0;
    const hasMissingFavicon = hasCustomFavicon === false;
    const hasPublishWarnings =
        hasMissingAltText || hasHeavyImages || hasMissingFavicon;
    const publishButtonClass = hasPublishWarnings
        ? `${
              buttonClass ||
              'rounded-full p-1.5 text-white transition-all focus:outline-none'
          } !bg-amber-400 !text-neutral-900 hover:!bg-amber-500`
        : `${
              buttonClass ||
              'rounded-full p-1.5 text-white transition-all focus:outline-none'
          } !bg-emerald-600 !text-white hover:!bg-emerald-700`;

    useEffect(() => setMounted(true), []);

    useEffect(
        () => () => {
            publishAttemptRef.current += 1;
        },
        []
    );

    useEffect(() => {
        let cancelled = false;
        const attempt = publishAttemptRef.current;

        API.get(`${company_id}/publication-status`)
            .then((status) => {
                if (
                    !cancelled &&
                    publishAttemptRef.current === attempt &&
                    status
                ) {
                    setPublishPhase(
                        status.published ? 'published' : 'unpublished'
                    );
                    setCanUnpublish(status.canUnpublish === true);
                    setPublicationStatusKnown(true);
                }
            })
            .catch((error) => {
                console.warn(
                    '[Publish] Unable to load publication status.',
                    error
                );
            });

        return () => {
            cancelled = true;
        };
    }, [company_id]);

    useEffect(() => {
        const handleOpenAltText = (event) => {
            const focus =
                event.detail?.focus === 'heavy' ? 'heavy' : 'alt-text';
            setAltTextPanelFocus(focus);
            setAltTextPanelOpen(true);
        };

        window.addEventListener('nicejob:open-alt-text', handleOpenAltText);
        return () =>
            window.removeEventListener(
                'nicejob:open-alt-text',
                handleOpenAltText
            );
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadFaviconStatus = () => {
            fetch('/api/favicon-status')
                .then((response) => {
                    if (!response.ok)
                        throw new Error('Unable to audit favicon');
                    return response.json();
                })
                .then((status) => {
                    if (!cancelled) {
                        setHasCustomFavicon(status?.hasCustomFavicon === true);
                    }
                })
                .catch((error) => {
                    console.warn('[Publish] Favicon audit failed.', error);
                });
        };
        const handleFaviconUpdated = () => setHasCustomFavicon(true);

        loadFaviconStatus();
        window.addEventListener('editor:favicon-updated', handleFaviconUpdated);
        return () => {
            cancelled = true;
            window.removeEventListener(
                'editor:favicon-updated',
                handleFaviconUpdated
            );
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const libraryImages = Array.isArray(editor.site.data?.images)
            ? editor.site.data.images
            : [];

        Promise.all(
            usedImages.map(async (image) => {
                const libraryImage = libraryImages.find(
                    (candidate) =>
                        String(candidate?.url || '').trim() === image.url
                );
                const librarySize = readImageSizeBytes(libraryImage);
                if (librarySize) return [image.url, librarySize];

                try {
                    const response = await fetch(
                        `/api/image-metadata?url=${encodeURIComponent(
                            image.url
                        )}`
                    );
                    if (!response.ok) return [image.url, 0];
                    const metadata = await response.json();
                    return [image.url, Number(metadata?.sizeBytes || 0)];
                } catch {
                    return [image.url, 0];
                }
            })
        ).then((entries) => {
            if (!cancelled) setImageSizesByUrl(Object.fromEntries(entries));
        });

        return () => {
            cancelled = true;
        };
    }, [editor.site.data?.images, usedImageUrlsKey]);

    useEffect(() => {
        let cancelled = false;
        fetch('/api/editor-alt-text-sources')
            .then((response) => {
                if (!response.ok)
                    throw new Error('Unable to audit site images');
                return response.json();
            })
            .then((payload) => {
                if (!cancelled && Array.isArray(payload?.sources)) {
                    setAltTextSources(payload.sources);
                }
            })
            .catch((error) => {
                console.warn(
                    '[Publish] Site-wide alt text audit failed.',
                    error
                );
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handlePublish = async (closePanel) => {
        const environments = ['production', 'staging'].filter(
            (environment) => publishDestinations[environment]
        );
        if (!environments.length) {
            notifications.push({
                title: 'Select a publish destination',
                variant: 'error',
            });
            return;
        }
        closePanel?.();
        const attempt = publishAttemptRef.current + 1;
        publishAttemptRef.current = attempt;
        setLoading(true);
        setPublishPhase('publishing');
        try {
            for (const environment of environments) {
                const result = await API.post(`${company_id}/publish`, {
                    env: environment,
                });
                if (!result?.deploymentId) {
                    throw new Error('Publish did not return a deployment ID');
                }

                await waitForPublishDeployment(
                    company_id,
                    result.deploymentId,
                    () => publishAttemptRef.current === attempt
                );
            }
            if (publishAttemptRef.current !== attempt) return;

            setPublishPhase('success');
            if (publishDestinations.production) {
                setCanUnpublish(true);
                setPublicationStatusKnown(true);
            }
            notifications.push({
                title: 'Publishing complete!',
                message:
                    environments.length > 1
                        ? 'Live site and preview site published.'
                        : environments[0] === 'staging'
                        ? 'Preview site published.'
                        : 'Live site published.',
                variant: 'success',
            });
        } catch (error) {
            if (publishAttemptRef.current !== attempt) return;
            setPublishPhase('idle');
            const isDnsLimit = error?.code === 'CLOUDFLARE_DNS_RECORD_LIMIT';
            notifications.push({
                title: isDnsLimit
                    ? 'Cloudflare DNS limit reached'
                    : 'Failed to publish',
                message: isDnsLimit
                    ? 'Remove unused preview-site records in Cloudflare, then try again.'
                    : undefined,
                variant: 'error',
                error,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUnpublish = async () => {
        setUnpublishing(true);
        try {
            await API.delete(`${company_id}/publish`);
            setPublishPhase('unpublished');
            setCanUnpublish(false);
            setPublicationStatusKnown(true);
            setUnpublishOpen(false);
            notifications.push({
                title: 'Site unpublished',
                variant: 'success',
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to unpublish',
                variant: 'error',
                error,
            });
        } finally {
            setUnpublishing(false);
        }
    };

    const updatePanelPos = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const panelWidth = 384; // matches w-96
        const left = Math.min(
            Math.max(16, rect.right - panelWidth),
            typeof window !== 'undefined'
                ? window.innerWidth - panelWidth - 16
                : rect.right - panelWidth
        );
        const top = rect.bottom + 16;
        setPanelPos({ top, left });
    };

    useEffect(() => {
        const onResize = () => updatePanelPos();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    return (
        <>
            <Popover className="relative">
                {({ open, close }) => {
                    const portalTarget = buttonRef.current?.ownerDocument?.body;
                    return (
                        <>
                            <PublishOutsideClick
                                open={open}
                                close={close}
                                buttonRef={buttonRef}
                                panelRef={panelRef}
                            />

                            {/* 🚀 Rocket Button */}
                            <Popover.Button
                                ref={buttonRef}
                                disabled={loading}
                                onClick={() => {
                                    if (!open) updatePanelPos();
                                }}
                                onFocus={() => {
                                    if (!open) updatePanelPos();
                                }}
                                className={`${publishButtonClass} overflow-hidden disabled:cursor-wait`}
                                title={
                                    loading
                                        ? 'Publishing to the live site'
                                        : publishPhase === 'success'
                                        ? 'Published'
                                        : publishPhase === 'published'
                                        ? 'Published'
                                        : hasPublishWarnings
                                        ? 'SEO recommendations need attention'
                                        : 'Publish'
                                }
                                aria-label={
                                    loading ? 'Publishing site' : 'Publish site'
                                }
                            >
                                <span
                                    className="block h-4 w-4"
                                    aria-hidden="true"
                                >
                                    <span
                                        className={`block h-4 w-4 ${
                                            publishPhase === 'publishing'
                                                ? 'publish-rocket-publishing'
                                                : publishPhase === 'success'
                                                ? `publish-rocket-success ${
                                                      hasPublishWarnings
                                                          ? 'publish-rocket-success-on-warning'
                                                          : 'publish-rocket-success-on-green'
                                                  }`
                                                : publishPhase === 'published'
                                                ? `publish-rocket-published ${
                                                      hasPublishWarnings
                                                          ? 'publish-rocket-success-on-warning'
                                                          : 'publish-rocket-success-on-green'
                                                  }`
                                                : ''
                                        }`}
                                    >
                                        {icon || (
                                            <RocketLaunchIcon className="h-4 w-4" />
                                        )}
                                    </span>
                                </span>
                            </Popover.Button>

                            <style>{`
                                .publish-rocket-publishing {
                                    animation: publish-rocket-shake 0.34s
                                        ease-in-out infinite;
                                    transform-origin: 50% 55%;
                                }

                                .publish-rocket-success {
                                    animation: publish-rocket-launch 1.55s
                                        ease-in-out both;
                                }

                                .publish-rocket-success-on-warning {
                                    --publish-success-color: #047857;
                                }

                                .publish-rocket-success-on-green {
                                    --publish-success-color: #a7f3d0;
                                }

                                .publish-rocket-published {
                                    color: var(--publish-success-color);
                                    transform: rotate(-45deg);
                                }

                                @keyframes publish-rocket-shake {
                                    0%,
                                    100% {
                                        transform: translate3d(0, -0.5px, 0)
                                            rotate(-45deg);
                                    }
                                    25% {
                                        transform: translate3d(1px, 1px, 0)
                                            rotate(-42deg);
                                    }
                                    75% {
                                        transform: translate3d(-1px, 0, 0)
                                            rotate(-48deg);
                                    }
                                }

                                @keyframes publish-rocket-launch {
                                    0% {
                                        color: inherit;
                                        opacity: 1;
                                        transform: translateY(0)
                                            rotate(-45deg);
                                    }
                                    38% {
                                        color: inherit;
                                        opacity: 1;
                                        transform: translateY(-190%)
                                            rotate(-45deg);
                                    }
                                    39% {
                                        color: var(--publish-success-color);
                                        opacity: 0;
                                        transform: translateY(-190%)
                                            rotate(-45deg);
                                    }
                                    55% {
                                        color: var(--publish-success-color);
                                        opacity: 0;
                                        transform: translateY(190%)
                                            rotate(-45deg);
                                    }
                                    56% {
                                        color: var(--publish-success-color);
                                        opacity: 1;
                                        transform: translateY(190%)
                                            rotate(-45deg);
                                    }
                                    88%,
                                    100% {
                                        color: var(--publish-success-color);
                                        opacity: 1;
                                        transform: translateY(0)
                                            rotate(-45deg);
                                    }
                                }

                                @media (prefers-reduced-motion: reduce) {
                                    .publish-rocket-publishing {
                                        animation: none;
                                        transform: rotate(-45deg);
                                    }

                                    .publish-rocket-success {
                                        animation: none;
                                        color: var(--publish-success-color);
                                        transform: rotate(-45deg);
                                    }
                                }
                            `}</style>

                            {mounted &&
                                portalTarget &&
                                createPortal(
                                    <Transition
                                        show={open}
                                        as={Fragment}
                                        enter="transition ease-out duration-150"
                                        enterFrom="opacity-0 translate-y-1"
                                        enterTo="opacity-100 translate-y-0"
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100 translate-y-0"
                                        leaveTo="opacity-0 translate-y-1"
                                    >
                                        <Popover.Panel
                                            ref={panelRef}
                                            static
                                            className="z-[20000] w-96 max-w-[calc(100vw-2rem)] rounded-md bg-neutral-800 p-3 shadow-lg ring-1 ring-black/30"
                                            style={{
                                                position: 'fixed',
                                                top: panelPos.top,
                                                left: panelPos.left,
                                            }}
                                        >
                                            <h4 className="mb-1 text-[11px] font-semibold text-neutral-400">
                                                Publish destination
                                            </h4>

                                            <div className="flex flex-col gap-1.5">
                                                {/* Staging preview */}
                                                <label
                                                    htmlFor="publish-staging"
                                                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-700"
                                                >
                                                    <input
                                                        id="publish-staging"
                                                        type="checkbox"
                                                        checked={
                                                            publishDestinations.staging
                                                        }
                                                        onChange={() =>
                                                            setPublishDestinations(
                                                                (current) => ({
                                                                    ...current,
                                                                    staging:
                                                                        !current.staging,
                                                                })
                                                            )
                                                        }
                                                        className="h-3.5 w-3.5 accent-blue-600"
                                                    />
                                                    <div className="flex min-w-0 flex-1 flex-col leading-tight">
                                                        <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-white">
                                                            <span className="break-all">
                                                                {previewDomain}
                                                            </span>
                                                            <a
                                                                href={`https://${previewDomain}/`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(
                                                                    event
                                                                ) =>
                                                                    event.stopPropagation()
                                                                }
                                                                className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-neutral-300 transition-colors hover:text-[#2CE080] focus:outline-none"
                                                                title="Open preview site in a new tab"
                                                                aria-label={`Open ${previewDomain} in a new tab`}
                                                            >
                                                                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                            </a>
                                                        </span>
                                                        <span className="text-[11px] text-neutral-500">
                                                            Preview site to
                                                            share with the
                                                            client
                                                        </span>
                                                    </div>
                                                </label>

                                                {/* Live */}
                                                <label
                                                    htmlFor="publish-live"
                                                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-700"
                                                >
                                                    <input
                                                        id="publish-live"
                                                        type="checkbox"
                                                        checked={
                                                            publishDestinations.production
                                                        }
                                                        onChange={() =>
                                                            setPublishDestinations(
                                                                (current) => ({
                                                                    ...current,
                                                                    production:
                                                                        !current.production,
                                                                })
                                                            )
                                                        }
                                                        className="h-3.5 w-3.5 accent-green-600"
                                                    />
                                                    <div className="flex min-w-0 flex-1 flex-col leading-tight">
                                                        <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-white">
                                                            <span className="break-all">
                                                                {liveDomain}
                                                            </span>
                                                            {liveUrl && (
                                                                <a
                                                                    href={
                                                                        liveUrl
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={(
                                                                        event
                                                                    ) =>
                                                                        event.stopPropagation()
                                                                    }
                                                                    className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-neutral-300 transition-colors hover:text-[#2CE080] focus:outline-none"
                                                                    title="Open live site in a new tab"
                                                                    aria-label={`Open ${liveDomain} in a new tab`}
                                                                >
                                                                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                                </a>
                                                            )}
                                                        </span>
                                                        <span className="text-[11px] text-neutral-400">
                                                            {publishPhase ===
                                                            'unpublished'
                                                                ? 'Unpublished'
                                                                : 'Live site'}
                                                        </span>
                                                    </div>
                                                </label>
                                            </div>

                                            {hasPublishWarnings && (
                                                <div className="mt-3 border-t border-neutral-700 pt-3">
                                                    <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-300">
                                                        <ExclamationCircleIcon className="h-4 w-4" />
                                                        SEO Recommendations
                                                    </h4>
                                                    {hasMissingAltText && (
                                                        <div>
                                                            <p className="mt-1.5 text-[12px] leading-4 text-neutral-200">
                                                                {
                                                                    missingAltTextCount
                                                                }{' '}
                                                                {missingAltTextCount ===
                                                                1
                                                                    ? 'image is'
                                                                    : 'images are'}{' '}
                                                                missing alt
                                                                text.
                                                            </p>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    close();
                                                                    setAltTextPanelFocus(
                                                                        'alt-text'
                                                                    );
                                                                    setAltTextPanelOpen(
                                                                        true
                                                                    );
                                                                }}
                                                                className="mt-1.5 inline-flex items-center rounded bg-amber-400 px-2 py-1 text-[10px] font-semibold leading-none text-neutral-900 shadow-sm transition-colors hover:bg-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-1 focus:ring-offset-neutral-800"
                                                            >
                                                                Fix missing alt
                                                                text
                                                            </button>
                                                        </div>
                                                    )}
                                                    {hasHeavyImages && (
                                                        <div
                                                            className={
                                                                hasMissingAltText
                                                                    ? 'mt-2.5 border-t border-neutral-700 pt-2.5'
                                                                    : ''
                                                            }
                                                        >
                                                            <p className="text-[12px] leading-4 text-neutral-200">
                                                                {
                                                                    heavyImageCount
                                                                }{' '}
                                                                {heavyImageCount ===
                                                                1
                                                                    ? 'image is'
                                                                    : 'images are'}{' '}
                                                                too heavy.
                                                            </p>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    close();
                                                                    setAltTextPanelFocus(
                                                                        'heavy'
                                                                    );
                                                                    setAltTextPanelOpen(
                                                                        true
                                                                    );
                                                                }}
                                                                className="mt-1.5 inline-flex items-center rounded bg-red-500 px-2 py-1 text-[10px] font-semibold leading-none text-white shadow-sm transition-colors hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-1 focus:ring-offset-neutral-800"
                                                            >
                                                                Review heavy
                                                                images
                                                            </button>
                                                        </div>
                                                    )}
                                                    {hasMissingFavicon && (
                                                        <div
                                                            className={
                                                                hasMissingAltText ||
                                                                hasHeavyImages
                                                                    ? 'mt-2.5 border-t border-neutral-700 pt-2.5'
                                                                    : ''
                                                            }
                                                        >
                                                            <p className="text-[12px] leading-4 text-neutral-200">
                                                                A custom favicon
                                                                has not been
                                                                uploaded.
                                                            </p>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    close();
                                                                    editor.site.open();
                                                                }}
                                                                className="mt-1.5 inline-flex items-center rounded bg-amber-400 px-2 py-1 text-[10px] font-semibold leading-none text-neutral-900 shadow-sm transition-colors hover:bg-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-1 focus:ring-offset-neutral-800"
                                                            >
                                                                Upload favicon
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="mt-3 flex items-center justify-between border-t border-neutral-700 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        close();
                                                        setUnpublishOpen(true);
                                                    }}
                                                    disabled={
                                                        loading ||
                                                        unpublishing ||
                                                        !publicationStatusKnown ||
                                                        !canUnpublish
                                                    }
                                                    className="rounded-md bg-red-600 px-2.5 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                                                >
                                                    Unpublish
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        handlePublish(close)
                                                    }
                                                    disabled={loading}
                                                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-semibold disabled:opacity-50 ${
                                                        hasPublishWarnings
                                                            ? 'bg-amber-400 text-neutral-900 hover:bg-amber-500'
                                                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                                    }`}
                                                >
                                                    {loading ? (
                                                        <svg
                                                            className="h-3.5 w-3.5 animate-spin"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            aria-hidden="true"
                                                        >
                                                            <circle
                                                                className="opacity-25"
                                                                cx="12"
                                                                cy="12"
                                                                r="10"
                                                                stroke="currentColor"
                                                                strokeWidth="4"
                                                            />
                                                            <path
                                                                className="opacity-75"
                                                                fill="currentColor"
                                                                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                                            />
                                                        </svg>
                                                    ) : (
                                                        <RocketLaunchIcon className="h-3.5 w-3.5" />
                                                    )}
                                                    {loading
                                                        ? 'Publishing…'
                                                        : 'Publish'}
                                                </button>
                                            </div>
                                        </Popover.Panel>
                                    </Transition>,
                                    portalTarget
                                )}
                        </>
                    );
                }}
            </Popover>
            <ConfirmationModal
                title="Unpublish live site?"
                variant="danger"
                message={
                    <div className="space-y-2 text-sm text-gray-500">
                        <p>
                            This will detach{' '}
                            <strong className="font-semibold text-gray-700">
                                {apexLiveDomain}
                            </strong>{' '}
                            and{' '}
                            <strong className="font-semibold text-gray-700">
                                www.{apexLiveDomain}
                            </strong>{' '}
                            from this Vercel project.
                        </p>
                        <p>
                            The project, deployments, domain account, and
                            NiceJob preview links will be preserved. Publishing
                            again will reconnect the domains.
                        </p>
                    </div>
                }
                prompt="Unpublish"
                open={unpublishOpen}
                onClose={() => {
                    if (!unpublishing) setUnpublishOpen(false);
                }}
                onConfirm={handleUnpublish}
                loading={unpublishing}
            />
            <AltTextEditor
                images={usedImages}
                imageSizesByUrl={imageSizesByUrl}
                sources={effectiveAltTextSources}
                focusMode={altTextPanelFocus}
                open={altTextPanelOpen}
                onClose={() => setAltTextPanelOpen(false)}
                onSourcesSaved={setAltTextSources}
                onImageSizeChange={(oldUrl, newUrl, sizeBytes) => {
                    setImageSizesByUrl((current) => {
                        const next = { ...current };
                        delete next[oldUrl];
                        next[newUrl] = sizeBytes;
                        return next;
                    });
                }}
            />
        </>
    );
}
